import React, { useEffect, useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { useBuilding } from "../../context/BuildingContext";
import { useUser } from "../../context/UserContext";
import { fetchUsersForBuilding } from "../../utils/firestoreHelpers";
import { colors, radius, shadows } from "../../utils/designTokens";
import { FaEye, FaSearch, FaUserCircle } from "react-icons/fa";

// fetchUsersForBuilding (vía fetchUsersByUserIds) completa el email faltante con
// "Sin email": sin un email real no se puede entrar "como" esa persona (la
// identidad sale del email).
const esEmailReal = (v) => /^[^\s@]+@[^\s@]+$/.test(String(v || "").trim());

/**
 * Herramienta admin: "Ver como residente" (solo lectura).
 * Lista los residentes del edificio seleccionado (en el header) y permite
 * "entrar como" uno de ellos para ver la plataforma tal cual la ve el vecino,
 * sin poder ejecutar acciones. NO cambia la sesión de Firebase (overlay).
 *
 * El alcance queda acotado por el propio selector de edificio del header:
 * el admin solo ve sus edificios; el superadmin, cualquiera.
 *
 * C10 (reglas 0D): las fichas de los residentes se leen con el edificio
 * (fetchUsersForBuilding → accessBids array-contains + userid in). Una ficha
 * que todavía no tiene el edificio en accessBids no aparece.
 *
 * @param {(payload:{email,userid,name,buildingid})=>void} onEnter
 */
export default function VerComoResidente({ onEnter }) {
  const { selectedBuilding } = useBuilding();
  const { userid: adminUserid } = useUser();
  const [residentes, setResidentes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorCarga, setErrorCarga] = useState(false);
  const [q, setQ] = useState("");
  const [entering, setEntering] = useState(null);

  const buildingId = selectedBuilding?.buildingid;

  useEffect(() => {
    if (buildingId == null) { setResidentes([]); setErrorCarga(false); return; }
    let alive = true;
    setLoading(true);
    setErrorCarga(false);
    (async () => {
      try {
        const users = await fetchUsersForBuilding(buildingId);
        // Solo residentes (typeuser 0) no fusionados, con email para impersonar.
        const res = users
          .filter((u) => Number(u.typeuser ?? 0) === 0 && u.mergedIntoUserid == null && esEmailReal(u.email))
          .sort((a, b) => (a.NombreCompleto || a.name || a.email).localeCompare(b.NombreCompleto || b.name || b.email));
        if (alive) setResidentes(res);
      } catch (e) {
        console.error("VerComoResidente: error cargando residentes", e?.code || e?.message || e);
        if (alive) {
          setResidentes([]);
          setErrorCarga(true);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [buildingId]);

  const filtrados = residentes.filter((u) => {
    if (!q.trim()) return true;
    const s = `${u.NombreCompleto || ""} ${u.ApellidosCompleto || ""} ${u.name || ""} ${u.email || ""} ${u.Documento || ""}`.toLowerCase();
    return s.includes(q.trim().toLowerCase());
  });

  const entrar = async (u) => {
    const email = String(u.email || "").toLowerCase();
    if (!email) return;
    setEntering(email);
    const name = u.NombreCompleto || u.name || email;
    // Auditoría (Ley 18.331): quién vio a quién, edificio, cuándo. Best-effort.
    try {
      await addDoc(collection(db, "audit"), {
        action: "view_as_resident",
        adminEmail: (auth.currentUser?.email || "").toLowerCase(),
        adminUserid: adminUserid != null ? String(adminUserid) : null,
        targetEmail: email,
        targetUserid: u.userid != null ? String(u.userid) : null,
        buildingid: buildingId != null ? Number(buildingId) : null,
        at: serverTimestamp(),
      });
    } catch (e) { console.warn("audit view_as_resident falló (no bloquea):", e?.message); }
    onEnter && onEnter({ email, userid: u.userid, name, buildingid: buildingId });
  };

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <FaEye size={20} color={colors.primary} />
        <div>
          <h1 style={S.title}>Ver como residente</h1>
          <p style={S.subtitle}>
            Entrá a la plataforma tal como la ve un vecino de <strong>solo lectura</strong>: ves todo lo suyo pero no podés ejecutar acciones. No cambia tu sesión.
          </p>
        </div>
      </div>

      {buildingId == null ? (
        <div style={S.empty}>Seleccioná un edificio en la barra de arriba para listar sus residentes.</div>
      ) : (
        <>
          <div style={S.searchWrap}>
            <FaSearch size={13} color={colors.gray500} />
            <input
              style={S.searchInput}
              placeholder="Buscar por nombre, email o documento"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          {loading ? (
            <div style={S.empty}>Cargando residentes…</div>
          ) : errorCarga ? (
            <div role="alert" style={S.empty}>No pudimos cargar los residentes de este edificio. Reintentá en unos minutos.</div>
          ) : filtrados.length === 0 ? (
            <div style={S.empty}>No hay residentes para mostrar en este edificio.</div>
          ) : (
            <div style={S.list}>
              {filtrados.map((u) => {
                const name = u.NombreCompleto || u.name || u.email;
                return (
                  <div key={u.id || u.email} style={S.row}>
                    <FaUserCircle size={30} color={colors.gray300} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={S.rowName}>{name}</div>
                      <div style={S.rowSub}>{u.email}{u.Documento ? ` · ${u.Documento}` : ""}</div>
                    </div>
                    <button
                      type="button"
                      style={{ ...S.enterBtn, opacity: entering === u.email ? 0.6 : 1 }}
                      onClick={() => entrar(u)}
                      disabled={!!entering}
                    >
                      <FaEye size={12} /> Entrar (solo lectura)
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const S = {
  wrap: { padding: 24, maxWidth: 900, margin: "0 auto" },
  header: { display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 18 },
  title: { margin: 0, fontSize: 22, fontWeight: 400, color: colors.gray900 },
  subtitle: { margin: "4px 0 0", fontSize: 14, color: colors.gray600, lineHeight: 1.5, maxWidth: 620 },
  empty: { padding: 28, textAlign: "center", color: colors.gray500, fontSize: 14, background: colors.white, borderRadius: radius.md, border: `1px solid ${colors.gray200}` },
  searchWrap: { display: "flex", alignItems: "center", gap: 8, padding: "0 12px", height: 42, background: colors.white, border: `1px solid ${colors.gray300}`, borderRadius: radius.sm, marginBottom: 14 },
  searchInput: { flex: 1, border: "none", outline: "none", fontSize: 15, fontFamily: "inherit", background: "transparent", color: colors.gray900 },
  list: { display: "flex", flexDirection: "column", gap: 8 },
  row: { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: colors.white, borderRadius: radius.md, border: `1px solid ${colors.gray200}`, boxShadow: shadows.sm },
  rowName: { fontSize: 15, color: colors.gray900, fontWeight: 400 },
  rowSub: { fontSize: 12.5, color: colors.gray500, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  enterBtn: { flexShrink: 0, display: "flex", alignItems: "center", gap: 6, height: 38, padding: "0 14px", background: colors.primary, color: colors.white, border: "none", borderRadius: radius.sm, fontSize: 13.5, fontFamily: "inherit", cursor: "pointer" },
};
